import { useContext, useEffect, useRef, useState, useMemo } from 'react'
import { Formik, useFormikContext } from 'formik'
import { emitCustomEvent, useCustomEventListener } from 'react-custom-events'
import { StoreContext } from '~/store'
import { OtpFlow,  Fetcher, ViewStepper, ViewListener, CurrentView, ResetButton, Spinner } from '~/components'
import { StepperContext } from '~/components/ui/ViewStepper'
import { DebugListener } from '~/components/DebugPanel'
import { usePrevious, useDidMountEffect } from '~/utils/hooks'
import { getUserName } from '~/utils/format'
import { getEditableFields, fetchLocationInfo } from '~/lib/api'
import { transformBookingLocation, getVehicleOptionValues, resolvePaymentValues, isBookablePayment } from '~/utils/bookingToForm'
import { markBookingPlaced } from '~/lib/returnTrip'
import classnames from 'classnames/bind'
import { default as SelectLocations } from './SelectLocations'
import { default as SelectTime } from './SelectTime'
import { default as SelectHow } from './SelectHow'
import { default as ConfirmBooking } from './ConfirmBooking'
import { default as BookingDone } from './BookingDone'
import { default as ReturnTripBooking } from './ReturnTripBooking'
import * as styles from './index.module.css'
import { motion, AnimatePresence, AnimateSharedLayout, LayoutGroup } from "framer-motion";
import { useTranslation } from 'react-i18next'

const cx = classnames.bind(styles);

// The embed is permanently in fullscreen: there is no surrounding page to
// collapse back into, so the module keeps the "booking started" layout from the
// first render and never leaves it. See body.embed-mode in modern/index.css for
// the matching layout rules.
const isEmbed = () => !!window.EMBED_CONFIG?.isEmbed

const getPaymentType = account => {
  console.log('getPaymentType:', account)
  if (account?.type === 'creditcard') {
    return `CreditCard-${account.accountId}`
  }
  else if (account?.type === 'TSAB' || account?.type === 'TCA') {
    return `Account-${account.accountNumber}`
  }
  return 'DefaultNoSelect'
}

const BookingModule = () => {
  const { t } = useTranslation()
  const { preferredPaymentAccount, preferredPaymentAccountCompany, preferredPaymentMethod, preferredPaymentMethodCompany, isCompanyTrip, user } = useContext(StoreContext)
  const preferredPayment = isCompanyTrip ? preferredPaymentAccountCompany : preferredPaymentAccount;
  const preferredMethod = isCompanyTrip ? preferredPaymentMethodCompany : preferredPaymentMethod;
  console.log('preferredPayment:', preferredPayment, 'preferredMethod:', preferredMethod);
  const pt = preferredMethod === 'PayInCar' ? 'PayInCar' : getPaymentType(preferredPayment);
  console.log('getPaymentType:', pt);
  
  // Memoize initialValues to prevent unnecessary re-renders when isCompanyTrip changes
  // This prevents Formik from reinitializing and losing fullscreen state
  const initialValues = useMemo(() => ({
    autoValues: {
      pickupTime: null,
      serviceType: null,
      fixedPrice: null,
    },
    pickupTime: '',
    departure: '',
    destination: '', 
    payment: pt,
    account: preferredMethod === 'PayInCar' ? false : preferredPayment,
    intermediateStopLocations: [],

    serviceType: 'taxi',
    flightNumber: '',
    checkedInBaggage: false,
    passengerCount: 0,
    priceToken: '',
    pet: false,
    low: false,
    stationWagon: false,
    booster: 0,
    rearFacingChildSeat: false,
    electric: false,
    name: '',
    phone: '',
    generalComment: '',
    accountReference: '',
    company: isCompanyTrip && user?.companyConnections?.[0] ? user?.companyConnections?.[0] : false,
    isCompanyTrip: isCompanyTrip,
    fixedPrice: true,
    priceIsLoading: false,
    priceEstimate: '',
    originInfo: [],
    vouchers: [],
    voucherObjects: [],
    selectedProviderId: '',
    lockedProviderId: '', // In edit mode: the original booking's provider, cannot be changed
    availability: {
      direct: false,
      prebook: false,
    },
    availabilityIsLoading: false,
    priceType: false,
    region: null,
    // Edit mode fields
    pickupTimeInitialized: false, // true once SelectTime has applied the API-derived minimum
    bookingId: NaN, // NaN for new bookings, number for edits
    booking: null, // Full booking object in edit mode
    editableFields: [], // Array of editable field paths from API
    editableOptions: {}, // Object with editable vehicle options
    editToken: '', // Token from edit-estimate response
    // Return trip fields (docs/RETURNTRIP.md)
    originalBookingReference: null, // Tracking reference from the original booking's returnTripRules, sent with the booking request
    returnTripBooking: null, // The original booking the return trip was prefilled from (read-only details in the sheet)
    returnTripPayment: null // { payment, account } carried over from the original booking; the sheet keeps the form on it
  }), [pt, preferredMethod, preferredPayment, isCompanyTrip, user?.companyConnections])

  console.log('initialValues:', initialValues)

  const views = [
    { id: 'locations', component: SelectLocations },
    { id: 'time', component: SelectTime },
    { id: 'how', component: SelectHow },
    { id: 'otpflow', component: OtpFlow },
    { id: 'confirm', component: ConfirmBooking },
    { id: 'done', component: BookingDone },
    // Prefilled return trip sheet; entered via RETURN_TRIP_BOOKING, never by next() from 'done'
    { id: 'returntrip', component: ReturnTripBooking },
  ]

  const embed = isEmbed();
  const [fullscreen, setFullscreen] = useState(embed);
  const [bookingStarted, setBookingStarted] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const bookingStartedRef = useRef(false);
  // Use ref to preserve fullscreen state across re-renders when isCompanyTrip changes
  const fullscreenRef = useRef(embed);

  useEffect(() => {
    bookingStartedRef.current = bookingStarted;
  }, [bookingStarted]);

  useEffect(() => {
    fullscreenRef.current = fullscreen;
  }, [fullscreen]);

  // While a return trip is being prefilled (RETURN_TRIP_BOOKING until the next reset) the
  // module is not yet fullscreen, so lock the form explicitly: a reinitialize in that window
  // (e.g. the preferred payment saved by the booking just placed) would wipe the prefill.
  const returnTripRef = useRef(false);
  useCustomEventListener('RETURN_TRIP_BOOKING', () => { returnTripRef.current = true }, [])

  useCustomEventListener('RESET_FORM', () => {
    setBookingStarted(false);
    // Also update the ref right away: handleViewChange runs in a layout effect on the very
    // next render and must not see the stale "started" value (it would re-enter fullscreen).
    bookingStartedRef.current = false;
    returnTripRef.current = false;
    // In the embed there is nothing to collapse back into, so stay fullscreen.
    if (embed) return;
    setFullscreen(false);
    if (typeof APP !== 'undefined' && APP.trigger) {
      APP.trigger(APP.events.BOOKING_MODULE_END_FULLSCREEN);
    }
  },
    [setFullscreen, setBookingStarted, embed]
  )
  // Called from ViewListener's layout effect, synchronously (it used to run 50 ms later via
  // setTimeout, which painted the new view in the old layout for a frame and then reflowed it).
  const handleViewChange = (id, prevId) => {
    if(id !== 'locations' || bookingStartedRef.current) {
      setFullscreen(true);
      setBookingStarted(true);
      bookingStartedRef.current = true;
      emitCustomEvent('BOOKING_STARTED')
      if (typeof APP !== 'undefined' && APP.trigger) {
        APP.trigger(APP.events.BOOKING_MODULE_FULLSCREEN);
      }
    } else if (!embed) {
      setFullscreen(false);
      if (typeof APP !== 'undefined' && APP.trigger) {
        APP.trigger(APP.events.BOOKING_MODULE_END_FULLSCREEN);
      }
    }
    if (id === 'locations' || prevId === 'locations') {
      emitCustomEvent('DISPLAY_TRIP')
    }
  }

  const [isWaitingForPoll, setIsWaitingForPoll] = useState(false)
  
  // Listen for waiting for poll state to add overlay
  useCustomEventListener('BOOKING_WAITING_FOR_POLL', ({ waiting }) => {
    setIsWaitingForPoll(waiting || false)
  }, [])

  return (
    <>
      {isWaitingForPoll && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.0)',
            zIndex: 10000,
            pointerEvents: 'all',
            cursor: 'not-allowed'
          }}
        />
      )}
      <div
        className={cx(styles.bookingwidget, {fullscreen:fullscreen})}
      >
            <ViewStepper views={views} sendEvent>
              {({ View, id }) => {
                // Memoize formInitialValues to prevent Formik reinitialization when isCompanyTrip changes
                // This prevents fullscreen from being lost when switching between private/company
                const formInitialValues = useMemo(() => ({
                  ...initialValues
                }), [initialValues])

                // Check if we're in edit mode (editing an existing booking)
                const isInEditMode = !isNaN(formInitialValues.bookingId) && formInitialValues.bookingId > 0

                // Disable reinitialize on 'done' step to prevent form reset when booking is confirmed
                // Also disable reinitialize when we're already in fullscreen to prevent CSS issues
                // Also disable reinitialize when in edit mode to prevent form values from being reset while user is editing
                // This ensures booking details are preserved when navigating to confirmation
                //
                // The embed is fullscreen from the first render, so fullscreenRef would lock
                // the form immediately and initialValues (preferred payment, user details)
                // could never land after login. Gate on bookingStarted there instead — the
                // same point in the flow the fullscreen check stands in for elsewhere.
                const valuesLocked = (embed ? bookingStartedRef.current : fullscreenRef.current) || returnTripRef.current
                const shouldReinitialize = id !== 'done' && id !== 'returntrip' && !valuesLocked && !isInEditMode

                return (
                  <Formik
                    key="booking-form-stable" // Stable key to prevent unnecessary remounting that clears values
                    initialValues={formInitialValues}
                    enableReinitialize={shouldReinitialize}
                    validationSchema={typeof View.validationSchema === 'function' ? (formikValues) => {
                      // Re-evaluate validation schema with current form values
                      // This ensures it updates when availability data loads
                      return View.validationSchema(formikValues)
                    } : View.validationSchema}
                    validateOnChange={true}
                    validateOnBlur={id === 'locations'}
                    onSubmit={async (values) => {
                      console.log(values)
                    }}
                    onReset={() => {
                      if (typeof APP !== 'undefined' && APP.trigger) {
                        APP.trigger(APP.events.BOOKING_MODULE_SHOW_COVER)
                      }
                      emitCustomEvent('RESET_FORM')
                      emitCustomEvent('RESET_MAP')
                    }}
                  >
                    <>
                      <Fetcher />
                      <CurrentView currentView={id} fullscreen={fullscreen}/>
                      <ResetButton message={t('reset-booking-message')} />
                      <ViewListener onViewChange={handleViewChange} />
                      <FormikListener setIsLoadingEdit={setIsLoadingEdit} />
                      {process.env.NODE_ENV !== 'production' && <DebugListener id="main" />}
                    </>
                  </Formik>
                )
              }}
            </ViewStepper>

            {isLoadingEdit && (
              <div className={styles.loadingOverlay}>
                <Spinner />
              </div>
            )}

      </div>
    </>
  )
}

const FormikListener = ({ setIsLoadingEdit }) => {
  const { user, preferredPaymentAccount, preferredPaymentAccountCompany, preferredPaymentMethod, preferredPaymentMethodCompany, setCurrentPaymentAccount, isCompanyTrip, paymentAccounts } = useContext(StoreContext)
  const { values, dirty, setFieldValue, setValues, resetForm, initialValues } = useFormikContext()
  const { next, reset: resetStepper } = useContext(StepperContext)
  //const [bookingStarted, setBookingStarted] = useState(false);
  const prevDeparture = usePrevious(values.departure)
  const prevDestination = usePrevious(values.destination)
  const prevLocations = usePrevious([values.departure, ...values.intermediateStopLocations, values.destination])
  
  // Listen for pending booking confirmation
  // NOTE: This is a fallback handler. The main handler is in ConfirmBooking component.
  // This only handles cases where ConfirmBooking isn't mounted (shouldn't happen normally)
  useCustomEventListener('PENDING_BOOKING_CONFIRMED', ({ bookingId }) => {
    // Only handle if bookingId is not already set (fallback case)
    // The ConfirmBooking component should handle this normally
    if (isNaN(values?.bookingId)) {
      // Fallback: set bookingId and navigate if ConfirmBooking didn't handle it
      markBookingPlaced(bookingId)
      setFieldValue('bookingId', bookingId, false)
      // Use requestAnimationFrame to ensure setFieldValue completes before navigation
      requestAnimationFrame(() => {
        next('done')
      })
    }
  })
  const hasUpdatedUser = useRef(false)
  const isWaitingForAvailability = useRef(false)
  // Set while a return trip is being prefilled, so RESET_FORM's delayed preferred-payment
  // restore (300 ms) doesn't overwrite the payment carried over from the original booking.
  const returnTripPendingRef = useRef(false)

  // Update preferred acount and payment type on form reset
  useCustomEventListener('RESET_FORM', () => {
      console.log('RESET_FORM event received in FormikListener')
      returnTripPendingRef.current = false
      const preferredPayment = isCompanyTrip ? preferredPaymentAccountCompany : preferredPaymentAccount;
      const preferredMethod = isCompanyTrip ? preferredPaymentMethodCompany : preferredPaymentMethod;
      setCurrentPaymentAccount(null, false);
      setCurrentPaymentAccount(null, true);
      isWaitingForAvailability.current = false  // Cancel any pending navigation
      hasUpdatedUser.current = false  // Reset so user name/phone will be re-filled

      // Reset form to initial values
      const resetValues = { ...initialValues };
      resetValues.isCompanyTrip = values.isCompanyTrip; // Preserve company trip status
      resetValues.company = values.company; // Preserve company name if set

      console.log('Resetting form to initial values')
      resetForm({ values: resetValues })
      resetStepper()

      console.log('setting the default payment:', preferredPayment, 'preferredMethod:', preferredMethod)
      setTimeout(() => {
        if (returnTripPendingRef.current) return
        // Restore PayInCar if preferred
        if (preferredMethod === 'PayInCar') {
          setFieldValue('payment', 'PayInCar', false)
          setFieldValue('account', false, false)
        } else if (preferredPayment && preferredPayment !== false) {
          setFieldValue('payment', getPaymentType(preferredPayment), false)
          setFieldValue('account', preferredPayment, false)
        }
      }, 300)
    },
    [setFieldValue, resetForm, initialValues, resetStepper, preferredPaymentAccount, preferredPaymentAccountCompany, preferredPaymentMethod, preferredPaymentMethodCompany, setCurrentPaymentAccount, isCompanyTrip, values.isCompanyTrip, values.company]
  )

  // Handle edit booking event
  useCustomEventListener('EDIT_BOOKING', async (booking) => {
      console.log('=== EDIT_BOOKING event received ===')
      console.log('Booking data:', JSON.stringify(booking, null, 2))

      // Trigger fullscreen immediately so loading spinner covers the whole page
      if (typeof APP !== 'undefined' && APP.trigger) {
        APP.trigger(APP.events.BOOKING_MODULE_FULLSCREEN)
      }
      emitCustomEvent('BOOKING_STARTED')

      setIsLoadingEdit(true)

      try {
        // Fetch editable fields from API
        const editableData = await getEditableFields(booking.id)
        console.log('=== Editable fields response ===')
        console.log(JSON.stringify(editableData, null, 2))

        // Map booking data to form values
        const hasFlightInfo = booking.originLocation?.flightInfo !== undefined
        const pickupTime = booking.bookingType === 'PRE_BOOKING' ? new Date(booking.pickUpTimeStamp) : ''

        // Service type (estate = taxi + stationWagon), add-ons and payment — shared with the return trip prefill
        const { serviceType, pet, low, stationWagon, booster: boosterSeats, rearFacingChildSeat, electric } = getVehicleOptionValues(booking)
        const { payment, account } = resolvePaymentValues(booking, user, paymentAccounts)

        console.log('=== Setting form values ===')
        console.log('bookingId:', booking.id)
        console.log('departure:', booking.originLocation)
        console.log('destination:', booking.destinationLocation)
        console.log('intermediateStopLocations:', booking.intermediateStopLocations)
        console.log('pickupTime:', pickupTime)
        console.log('serviceType:', serviceType)
        console.log('payment:', payment)
        console.log('vehicleOptions - pet:', pet, 'low:', low, 'stationWagon:', stationWagon, 'booster:', boosterSeats, "rearFacingChildSeat:", rearFacingChildSeat, 'electric:', electric)
        console.log('hasFlightInfo:', hasFlightInfo)

        // Transform booking locations to form format
        // Booking API returns flat structure, but form expects nested address object
        const transformedDeparture = transformBookingLocation(booking.originLocation, hasFlightInfo)
        const transformedDestination = transformBookingLocation(booking.destinationLocation)
        const transformedIntermediateStops = (booking.intermediateStopLocations || []).map(loc => transformBookingLocation(loc))

        console.log('=== Transformed locations ===')
        console.log('transformedDeparture:', transformedDeparture)
        console.log('transformedDestination:', transformedDestination)

        // Set all form values for edit mode
        setFieldValue('bookingId', booking.id, false)
        setFieldValue('booking', booking, false) // Store the full booking object
        setFieldValue('editableFields', editableData.editableFields || [], false)
        // Lock to the original booking's provider so partner cannot be changed during edit
        const lockedProviderId = booking.providerData?.providerId || booking.providerId || ''
        setFieldValue('lockedProviderId', lockedProviderId, false)
        if (lockedProviderId) setFieldValue('selectedProviderId', lockedProviderId, false)
        setFieldValue('editableOptions', editableData.editableOptions || {}, false)
        setFieldValue('editableFieldsValidUntil', editableData.validUntil || null, false)

        setFieldValue('departure', transformedDeparture, false)
        setFieldValue('destination', transformedDestination, false)
        setFieldValue('intermediateStopLocations', transformedIntermediateStops, false)
        setFieldValue('pickupTime', pickupTime, false)
        setFieldValue('serviceType', serviceType, false)

        setFieldValue('name', booking.traveler?.name || '', false)
        setFieldValue('phone', booking.traveler?.phoneNumber || '', false)
        setFieldValue('generalComment', booking.generalComment || '', false)
        setFieldValue('accountReference', booking.invoiceReference || '', false)

        setFieldValue('flightNumber', hasFlightInfo ? booking.originLocation.flightInfo.flightNumber : '', false)
        setFieldValue('checkedInBaggage', hasFlightInfo ? booking.originLocation.flightInfo.checkedInBaggage : false, false)

        setFieldValue('pet', pet, false)
        setFieldValue('low', low, false)
        setFieldValue('stationWagon', stationWagon, false)
        setFieldValue('booster', boosterSeats, false)
        setFieldValue('rearFacingChildSeat', rearFacingChildSeat, false)
        setFieldValue('electric', electric, false)

        setFieldValue('payment', payment, false)
        setFieldValue('account', account, false)
        setFieldValue('company', booking.company || false, false)
        setFieldValue('isCompanyTrip', booking.company !== undefined, false)

        console.log('=== All form values set ===')
        console.log('Current form values:', values)

        // Set flag to navigate to 'how' view once availability data is loaded
        isWaitingForAvailability.current = true
        console.log('Set isWaitingForAvailability flag to true')

        // Navigate to locations view with pre-filled data
        // This triggers the Fetcher to load availability data
        // Note: BOOKING_STARTED and FULLSCREEN are now triggered at the start of EDIT_BOOKING
        setTimeout(() => {
          console.log('=== Form values ready, waiting for availability data ===')
          console.log('Form values after timeout:', values)
        }, 100)
      } catch (error) {
        console.error('=== Error loading booking for edit ===', error)
        setIsLoadingEdit(false)
      }
    },
    [setFieldValue, paymentAccounts, user, values, setIsLoadingEdit]
  )

  // Return trip (docs/RETURNTRIP.md): prefill the form from the original booking with the trip
  // reversed and open the return trip sheet. Mirrors the app's prepareReturnTripService:
  // original destination becomes pickup, original origin becomes destination, intermediate
  // stops reversed; car type, add-ons, passengers, company, reference and payment method are
  // carried over; the time (and flight info for an airport pickup) is left for the user.
  useCustomEventListener('RETURN_TRIP_BOOKING', async (booking) => {
      console.log('=== RETURN_TRIP_BOOKING event received ===', booking)
      if (!booking?.destinationLocation) return
      returnTripPendingRef.current = true

      if (typeof APP !== 'undefined' && APP.trigger) {
        APP.trigger(APP.events.BOOKING_MODULE_FULLSCREEN)
      }
      emitCustomEvent('BOOKING_STARTED')
      setIsLoadingEdit(true)

      try {
        // Resolve precise locations so the information tags (e.g. FLIGHT_NBR, which decides
        // whether the sheet asks for flight info) are present. Fall back to the booking's own
        // flat location if the lookup fails.
        const resolveLocation = async (loc) => {
          try {
            const full = await fetchLocationInfo(loc)
            // /locations/{id} answers with `locationId`; the form (and the availability query)
            // expects `id`, like LocationSearch's formatLocationWithId.
            if (full?.address?.streetName) {
              const { locationId, ...rest } = full
              return { id: locationId || loc.id, ...rest }
            }
          } catch (error) {
            console.error('Could not resolve location for return trip', error)
          }
          return transformBookingLocation(loc)
        }
        const [departure, destination, ...intermediateStopLocations] = await Promise.all([
          resolveLocation(booking.destinationLocation),
          resolveLocation(booking.originLocation),
          ...(booking.intermediateStopLocations || []).slice().reverse().map(resolveLocation),
        ])

        const vehicle = getVehicleOptionValues(booking)
        let { payment, account } = resolvePaymentValues(booking, user, paymentAccounts)
        // Klarna/Apple Pay/removed cards can't be booked with here; leave the payment unselected so
        // the sheet raises the "payment method not available" problem once availability is in.
        if (!isBookablePayment(payment, account)) {
          payment = 'DefaultNoSelect'
          account = false
        }
        // Keep the original trip's company (prefer the live connection for requireReference etc.)
        const company = booking.company
          ? (user?.companyConnections?.find(c => c.id === booking.company.id) || booking.company)
          : false

        setValues({
          ...values,
          bookingId: NaN,
          booking: null,
          editableFields: [],
          editableOptions: {},
          editToken: '',
          lockedProviderId: '',
          selectedProviderId: booking.providerData?.providerId || '',
          departure,
          destination,
          intermediateStopLocations,
          pickupTime: '',
          pickupTimeInitialized: false,
          flightNumber: '',
          checkedInBaggage: false,
          passengerCount: booking.traveler?.travelerCount || 0,
          ...vehicle,
          name: booking.traveler?.name || values.name || getUserName(user),
          phone: booking.traveler?.phoneNumber || values.phone || user?.phone || '',
          generalComment: '',
          accountReference: booking.invoiceReference || '',
          payment,
          account,
          company,
          isCompanyTrip: booking.company !== undefined,
          fixedPrice: true,
          vouchers: [],
          voucherObjects: [],
          priceEstimate: '',
          priceToken: '',
          priceType: false,
          autoValues: { pickupTime: null, serviceType: null, fixedPrice: null },
          availability: { direct: false, prebook: false },
          originalBookingReference: booking.returnTripRules?.originalBookingReference || null,
          returnTripBooking: booking,
          returnTripPayment: { payment, account },
        }, false)

        setTimeout(() => {
          next('returntrip')
          setIsLoadingEdit(false)
        }, 50)
      } catch (error) {
        console.error('=== Error preparing return trip ===', error)
        returnTripPendingRef.current = false
        setIsLoadingEdit(false)
      }
    },
    [setValues, values, user, paymentAccounts, next, setIsLoadingEdit]
  )

  // Update voucherObjects array when vouchers are changed
  // The objects are used to render details in the BookingSummary
  useEffect(() => {
    if (user && user.vouchers) {
      const voucherObjects = user.vouchers.filter(item => values.vouchers.includes(item.voucherCode))
      setFieldValue('voucherObjects', voucherObjects, false)
    }
  }, [values.vouchers])

  // Set username and phone if we have a logged in user
  useEffect(() => {
    if (user && (dirty === false || hasUpdatedUser.current === false)) {
      if (values.name === '') setFieldValue('name', getUserName(user), false)
      if (values.phone === '') setFieldValue('phone', user.phone, false)
      hasUpdatedUser.current =  true
    }
  }, [user, values, dirty, hasUpdatedUser, setFieldValue])

  // Auto-navigate to appropriate view once availability data is loaded in edit mode
  useEffect(() => {
    if (isWaitingForAvailability.current) {
      const key = values.pickupTime === '' ? 'direct' : 'prebook'
      const hasAvailability = values.availability?.[key]?.availabilities !== undefined

      console.log('=== Auto-navigation check ===')
      console.log('isWaitingForAvailability:', isWaitingForAvailability.current)
      console.log('pickupTime key:', key)
      console.log('hasAvailability:', hasAvailability)
      console.log('values.availability:', values.availability)
      console.log('values.departure:', values.departure)
      console.log('values.destination:', values.destination)
      console.log('values.serviceType:', values.serviceType)
      console.log('values.bookingId:', values.bookingId)
      console.log('editableFields:', values.editableFields)

      if (hasAvailability) {
        console.log('=== Availability data loaded! Determining navigation target ===')
        isWaitingForAvailability.current = false

        // Check if time or flight info fields are editable
        const isTimeEditable = values.editableFields?.includes('pickUpTimeStamp')
        const isFlightNumberEditable = values.editableFields?.includes('originLocation.flightInfo.flightNumber')
        const isCheckedBaggageEditable = values.editableFields?.includes('originLocation.flightInfo.checkedInBaggage')

        // Check if origin location has FLIGHT_NBR tag
        const hasFlightNumberTag = values.departure?.locationInformation?.some(
          info => info.tag === 'FLIGHT_NBR'
        ) || false

        // Show SelectTime if:
        // 1. pickUpTimeStamp is editable OR
        // 2. (flight info fields are editable AND origin has FLIGHT_NBR tag)
        const shouldStartOnTime = isTimeEditable ||
          ((isFlightNumberEditable || isCheckedBaggageEditable) && hasFlightNumberTag)

        console.log('isTimeEditable:', isTimeEditable)
        console.log('isFlightNumberEditable:', isFlightNumberEditable)
        console.log('isCheckedBaggageEditable:', isCheckedBaggageEditable)
        console.log('hasFlightNumberTag:', hasFlightNumberTag)
        console.log('shouldStartOnTime:', shouldStartOnTime)

        setTimeout(() => {
          if (shouldStartOnTime) {
            console.log('=== Navigating to SelectTime (time/flight info editable) ===')
            next('time')
          } else {
            console.log('=== Navigating to SelectHow (only vehicle options editable) ===')
            next('how')
          }
          setIsLoadingEdit(false)
        }, 100)
      } else {
        console.log('=== Still waiting for availability data ===')
      }
    }
  }, [values.availability, values.departure, values.destination, values.serviceType, values.bookingId, values.editableFields, next, setIsLoadingEdit])

  const hasValue = value => value !== '' && value !== '-' && value?.address !== undefined

  // Trigger events to update the map
  useDidMountEffect(() => {
    console.log('=== Location change detected ===')
    const locations = [values.departure, ...values.intermediateStopLocations, values.destination]
    console.log('Current locations:', locations)
    console.log('Previous locations:', prevLocations)

    const locationAdded =
      locations.length > prevLocations?.length ||
      locations.length === prevLocations?.length && locations.some((item, index) => hasValue(item) && !hasValue(prevLocations[index]))

    console.log('locationAdded:', locationAdded)
    // const locationRemoved = locations.length < prevLocations?.length || locations.length === prevLocations?.length && locations.some((item, index) => !hasValue(item) && hasValue(prevLocations[index]))
    // const orderChanged = locations.length === prevLocations?.length && !locationRemoved && locations.every(item => prevLocations.includes(item))

    // console.log(prevLocations)
    // console.log(locations)
    // console.log('locationAdded:', locationAdded)
    // console.log('locationRemoved:', locationRemoved)
    // console.log('orderChanged:', orderChanged)

    let indexAdded = null

    // If location is added, find index to zoom in on that item on the map
    if (locationAdded) {
      indexAdded = locations.findIndex((item, index) => item !== prevLocations[index]) 
      console.log('indexAdded:', indexAdded)
    }

    // Set destination INFO messages in originInfo (departure notifications are
    // handled directly by Notifications component using new tag logic).
    const destinationInfo = {title: values.destination?.name || values.destination?.address?.poiName, info: null};
      destinationInfo.info = values.destination?.locationInformation?.filter(item => ['INFO'].includes(item?.tag)).map(item => item.message) || [];
      setFieldValue('originInfo', [destinationInfo].filter(info => info.info.length > 0), false);

    // If destination is changed, update origin info
    if (values.departure?.address && values.departure !== prevDeparture) {
      console.log('=== departure changed')
      setFieldValue('originInfo', [destinationInfo].filter(info => info.info.length > 0), false)
    }
    // If departure is cleared, remove origin info
    else if (!hasValue(values.departure) && hasValue(prevDeparture)) {
      console.log('Clearing origininfo, and region')
      setFieldValue('originInfo', [], false)
      setFieldValue('region', null, false)
    }

    // Update map with locations, change any empty locations to null
    emitCustomEvent('LOCATION_CHANGE', { 
      locations: locations.map(item => !hasValue(item) ? null : item), 
      active: indexAdded 
    })

    // Zoom out and display trip
    if (!locationAdded) {
      emitCustomEvent('DISPLAY_TRIP')
    }
  }, [values.departure, values.destination, values.intermediateStopLocations])

  return null
}

export default BookingModule
